import axios from 'axios';
import {
    getOrCreateDeviceUuid,
    getDeviceRecord,
    saveDeviceEnrollment,
    saveDeviceKeyPair,
    exportDeviceKeyPair,
    clearDeviceCertificate,
    setDeviceBlocked,
} from '@/utils/posOffline/device';

const ALGO = { name: 'RSASSA-PKCS1-v1_5', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-512' };

/** Evita POST /devices/renew concurrentes o repetidos en el mismo ciclo de vida. */
const renewInFlightByNegocio = new Map();
const renewDoneUntilByNegocio = new Map();
const RENEW_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 h tras un renew exitoso

function bufToB64(buf) {
    const bytes = new Uint8Array(buf);
    let s = '';
    bytes.forEach((b) => { s += String.fromCharCode(b); });
    return btoa(s);
}

function certNeedsRenewal(certVenceAt, daysBefore = 30) {
    // Sin fecha local: renovar una vez (cooldown evita spam si no se guarda vence_at).
    if (!certVenceAt) return true;
    const vence = new Date(certVenceAt);
    if (Number.isNaN(vence.getTime())) return true;
    const msLeft = vence.getTime() - Date.now();
    return msLeft <= daysBefore * 24 * 60 * 60 * 1000;
}

export async function ensureDeviceKeyPair() {
    const existing = await exportDeviceKeyPair();
    if (existing && existing.privateKey && existing.publicKeySpki) {
        return existing;
    }
    const pair = await crypto.subtle.generateKey(ALGO, false, ['sign', 'verify']);
    const spki = await crypto.subtle.exportKey('spki', pair.publicKey);
    const record = {
        privateKey: pair.privateKey,
        publicKeySpki: bufToB64(spki),
        certificate: null,
    };
    await saveDeviceKeyPair(record);
    return record;
}

/**
 * Enrola este equipo para el negocio indicado.
 * Si ya está enrolado, no pega a /devices/renew (eso spameaba logs en cada tab/warm).
 * La renovación preventiva queda en renewCertificateIfNeeded.
 */
export async function enrollDeviceIfNeeded({ negocio, nombre, sucursal, force = false }) {
    if (!negocio) {
        throw new Error('Falta el negocio para enrolar el dispositivo.');
    }
    const keys = await ensureDeviceKeyPair();
    const dev = await getDeviceRecord(negocio);
    // Solo el slot de este negocio cuenta (no el certificado global de otra cuenta).
    const tieneEnrolamiento = !!(dev && dev.codigo && dev.certificate);

    if (!force && tieneEnrolamiento) {
        // Renovar solo si el cert está por vencer; si no, reutilizar local.
        if (certNeedsRenewal(dev.cert_vence_at, 30)) {
            const renewed = await renewCertificateIfNeeded({ negocio, daysBefore: 30 });
            return {
                ...keys,
                certificate: (renewed && renewed.certificate) || keys.certificate || dev.certificate,
                device: await getDeviceRecord(negocio),
            };
        }
        return {
            ...keys,
            certificate: keys.certificate || dev.certificate,
            device: dev,
        };
    }

    const device_uuid = await getOrCreateDeviceUuid();
    const { data } = await axios.post('/devices/enroll', {
        device_uuid,
        public_key_spki: keys.publicKeySpki,
        nombre: nombre || (dev && dev.nombre) || 'Caja 1',
        sucursal: sucursal || null,
        app_version: '1.0.0',
        usuario_negocio: negocio,
    });
    const cert = data.certificate;
    await saveDeviceKeyPair({ ...keys, certificate: cert });
    if (data.device) {
        await saveDeviceEnrollment({
            device_uuid: data.device.device_uuid,
            codigo: data.device.codigo,
            nombre: data.device.nombre,
            usuario_negocio: negocio,
            certificate: cert,
            cert_vence_at: data.device.cert_vence_at,
        });
    }
    return {
        ...keys,
        certificate: cert,
        device: await getDeviceRecord(negocio),
    };
}

export async function reEnrollPosDevice({ negocio, nombre, sucursal }) {
    await clearDeviceCertificate(negocio);
    renewDoneUntilByNegocio.delete(String(negocio));
    const keys = await enrollDeviceIfNeeded({
        negocio,
        nombre,
        sucursal,
        force: true,
    });
    await setDeviceBlocked(false);
    return keys;
}

/**
 * Renueva el certificado solo si está vencido, por vencer (default: 30 días)
 * o si aún no hay fecha de vencimiento local.
 * Deduplica concurrentes y aplica cooldown tras un renew exitoso.
 */
export async function renewCertificateIfNeeded({ negocio, daysBefore = 30 }) {
    if (!negocio) return exportDeviceKeyPair();
    const negocioKey = String(negocio);

    const pending = renewInFlightByNegocio.get(negocioKey);
    if (pending) return pending;

    const run = (async () => {
        const record = await exportDeviceKeyPair();
        const dev = await getDeviceRecord(negocio);
        if (!record || !dev || !dev.device_uuid) return record;

        if (!certNeedsRenewal(dev.cert_vence_at, daysBefore)) {
            return record;
        }

        const venceMs = dev.cert_vence_at ? new Date(dev.cert_vence_at).getTime() : NaN;
        const alreadyExpired = Number.isFinite(venceMs) && venceMs <= Date.now();
        const coolUntil = renewDoneUntilByNegocio.get(negocioKey) || 0;
        // Cooldown solo para renovaciones preventivas; si ya venció, siempre intentar.
        if (!alreadyExpired && Date.now() < coolUntil) {
            return record;
        }

        const { data } = await axios.post('/devices/renew', {
            device_uuid: dev.device_uuid,
            public_key_spki: record.publicKeySpki,
            app_version: '1.0.0',
            usuario_negocio: negocio,
        });
        await saveDeviceKeyPair({ ...record, certificate: data.certificate });
        await saveDeviceEnrollment({
            device_uuid: dev.device_uuid,
            codigo: dev.codigo,
            nombre: dev.nombre,
            usuario_negocio: negocio,
            certificate: data.certificate,
            cert_vence_at: data.venceAt,
        });
        renewDoneUntilByNegocio.set(negocioKey, Date.now() + RENEW_COOLDOWN_MS);
        return { ...record, certificate: data.certificate };
    })();

    renewInFlightByNegocio.set(negocioKey, run);
    try {
        return await run;
    } finally {
        if (renewInFlightByNegocio.get(negocioKey) === run) {
            renewInFlightByNegocio.delete(negocioKey);
        }
    }
}
